import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {DataTableColumn} from 'notebook/data-tables/DataTableConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {
  ResultSchemaPublishedDataTableColumnsQuery,
  ResultSchemaPublishedDataTableColumnsQueryVariables,
  ResultSchemaPublishedDataTableColumnsQuery_resultSchemaPublishedDataTableColumns_PublishedDataTableColumnsNode as PublishedDataTableColumnsNode,
} from 'runs/__generated/ResultSchemaPublishedDataTableColumnsQuery';
import {Optional} from 'util/Types';

export const RESULT_SCHEMA_PUBLISHED_DATA_TABLE_COLUMNS_QUERY: DocumentNode = gql`
  query ResultSchemaPublishedDataTableColumnsQuery($resultSchemaIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: RESULTS) {
    resultSchemaPublishedDataTableColumns(resultSchemaIds: $resultSchemaIds) {
      resultSchemaId
      publishedDataTableColumnsJson
    }
  }
`;

export default function withPublishedDataTableColumnsQuery<
  WrappedProps extends {
    publishedDataTableColumnsBySchema: {[schemaId: string]: Optional<Array<DataTableColumn>>};
  },
>(
  WrappedComponent: React.ComponentType<WrappedProps>,
  getResultSchemasFromProps: (props: WrappedProps) => Array<AssaySchema>
): React.FunctionComponent<Omit<WrappedProps, 'publishedDataTableColumnsBySchema'>> {
  return (props: WrappedProps) => {
    const schemas = getResultSchemasFromProps(props);
    return (
      <BenchlingQuery<
        ResultSchemaPublishedDataTableColumnsQuery,
        ResultSchemaPublishedDataTableColumnsQueryVariables
      >
        query={RESULT_SCHEMA_PUBLISHED_DATA_TABLE_COLUMNS_QUERY}
        variables={{resultSchemaIds: schemas.map((schema) => schema.id)}}
        skip={BenchlingConfig.RESULTS_SEPARATE_PUBLISHED_DATA_TABLE_COLUMNS_LOAD === 'OFF'}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      >
        {(data) => (
          <WrappedComponent
            {...props}
            publishedDataTableColumnsBySchema={
              BenchlingConfig.RESULTS_SEPARATE_PUBLISHED_DATA_TABLE_COLUMNS_LOAD === 'OFF'
                ? Object.fromEntries(schemas.map((schema) => [schema.id, schema.publishedDataTableColumns]))
                : Object.fromEntries(
                    (data?.resultSchemaPublishedDataTableColumns ?? []).map(
                      (pdtcNode: PublishedDataTableColumnsNode) => [
                        pdtcNode.resultSchemaId,
                        pdtcNode.publishedDataTableColumnsJson,
                      ]
                    )
                  )
            }
          />
        )}
      </BenchlingQuery>
    );
  };
}
